/**
 * Settings → AI's Providers card (slice 1c spec §6.1): a row per connected
 * provider. Closed, a row says how it connects, its fullest usage window (or,
 * for an API key, where its usage is), and its accounts' state; it opens by
 * itself when an account needs the owner or a connect runs. Open, it lists
 * the accounts with their usage and actions, and Add another account. What
 * each piece says comes from `ai-view.ts`.
 */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@crew/ui/components/alert-dialog";
import { Button } from "@crew/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@crew/ui/components/card";
import { Progress } from "@crew/ui/components/progress";
import { StatusBadge, StatusLabel } from "@crew/ui/components/status";
import { cn } from "@crew/ui/lib/utils";
import { ArrowUpRight, ChevronDown, ExternalLink, Plus } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import type { AiConnection, AiProvider, ConnectionUsage, Me } from "../../app/me";
import { useDisconnect, useMoveAccount } from "../setup/api";
import { PROVIDERS } from "../setup/onboarding";
import { ProviderLogo } from "../setup/provider-tiles";
import { Arrows, ErrorNote, Show } from "./ai-parts";
import {
  accountState,
  disconnectBlock,
  expiryNote,
  oneAccount,
  orderMoves,
  orderNote,
  replaceLabel,
  roleChips,
  rowStatus,
  rowSubtitle,
  rowUsage,
  switchesRoute,
  usageLink,
  usageView,
  type AccountState,
  type ProviderCard,
  type RowUsage,
} from "./ai-view";

type Ai = NonNullable<Me["computer"]>["ai"];

/** What the connect panel is open for: a provider, and the account a Replace is for. */
export type Open = { provider: AiProvider; account?: string; title: string };

/** An account's usage (§5.2): the plan, a bar per window, the balance, and when it was read. */
function UsageLines({
  usage,
  now,
  timezone,
}: {
  usage: ConnectionUsage;
  now: number;
  timezone: string;
}) {
  const view = usageView(usage, now, timezone);
  return (
    <div className="flex flex-col gap-1.5" data-usage>
      <Show when={view.plan}>
        <p className="text-meta text-copy">{view.plan}</p>
      </Show>
      {view.windows.map((w) => (
        <div
          key={w.label}
          data-usage-window={w.label}
          className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 font-mono text-2xs text-copy sm:grid-cols-[8.5rem_6rem_1fr]"
        >
          <span>{w.label}</span>
          <Progress
            value={w.pct}
            aria-label={`${w.label}, ${w.pct}% used`}
            className="w-24 sm:w-full [&_[data-slot=progress-indicator]]:bg-foreground"
          />
          <span className="col-span-2 text-muted-foreground sm:col-span-1">{w.text}</span>
        </div>
      ))}
      <Show when={view.summary}>
        <p className="font-mono text-2xs text-copy">{view.summary}</p>
      </Show>
      <p className="text-2xs text-muted-foreground">{view.asOf}</p>
    </div>
  );
}

/** The confirm before a Disconnect. */
function DisconnectConfirm({
  account,
  last,
  trigger,
  onConfirm,
}: {
  account: AiConnection;
  /** The workspace's only account (slice 1k spec §3.3): nothing answers until another connects. */
  last: boolean;
  trigger: ReactNode;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {trigger}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disconnect {account.label}?</AlertDialogTitle>
          <AlertDialogDescription>
            Your computer signs out of this {PROVIDERS[account.provider].name} account and forgets
            it.{" "}
            {last
              ? "Your crew can't answer until you connect an AI again."
              : "Fallback models that only it could serve are removed."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => {
              setOpen(false);
              onConfirm();
            }}
          >
            Disconnect
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Disconnect, with a confirm; disabled with why for the default's last account. */
function Disconnect({ account, ai, busy }: { account: AiConnection; ai: Ai; busy: boolean }) {
  const disconnect = useDisconnect();
  const blocked = disconnectBlock(account, ai);
  const why = `disconnect-why-${account.id}`;
  const trigger = (
    <AlertDialogTrigger
      render={
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          disabled={busy || blocked !== null || disconnect.isPending}
          aria-describedby={blocked ? why : undefined}
        />
      }
    >
      Disconnect
    </AlertDialogTrigger>
  );
  return (
    <>
      <DisconnectConfirm
        account={account}
        last={switchesRoute(account, ai)}
        trigger={trigger}
        onConfirm={() => disconnect.mutate(account.id)}
      />
      <Show when={blocked}>
        <p id={why} className="basis-full text-meta text-muted-foreground" data-disconnect-blocked>
          {blocked}
        </p>
      </Show>
      <ErrorNote error={disconnect.error} />
    </>
  );
}

/** Up and down in the order OpenClaw tries the provider's accounts (§3.3). */
function AccountOrder({ account, ai, busy }: { account: AiConnection; ai: Ai; busy: boolean }) {
  const move = useMoveAccount();
  const moves = orderMoves(account, ai);
  return (
    <Show when={moves.show}>
      <span className="ml-auto flex items-center gap-1">
        <Arrows
          name={{ up: `Try ${account.label} earlier`, down: `Try ${account.label} later` }}
          up={moves.up}
          down={moves.down}
          disabled={busy || move.isPending}
          onMove={(direction) => move.mutate({ id: account.id, direction })}
        />
      </span>
      <ErrorNote error={move.error} />
    </Show>
  );
}

/** Replace or Reconnect, Disconnect, and the order: none while the account connects. */
function AccountActions({
  account,
  ai,
  state,
  busy,
  onReplace,
}: {
  account: AiConnection;
  ai: Ai;
  state: AccountState;
  busy: boolean;
  onReplace: (verb: string) => void;
}) {
  const verb = replaceLabel(state);
  return (
    <Show when={state.key !== "connecting"}>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={verb === "Reconnect" ? "default" : "outline"}
          size="sm"
          disabled={busy}
          onClick={() => onReplace(verb)}
          aria-label={`${verb} ${account.label}`}
        >
          {verb}
        </Button>
        <Disconnect account={account} ai={ai} busy={busy} />
        <AccountOrder account={account} ai={ai} busy={busy} />
      </div>
    </Show>
  );
}

/** A setup token nearing the end of its year (slice 1g spec §6). */
function ExpiryNote({
  account,
  now,
  timezone,
}: {
  account: AiConnection;
  now: number;
  timezone: string;
}) {
  const note = expiryNote(account, now, timezone);
  return note ? (
    <p className="text-meta text-copper" data-token-expiry>
      {note}
    </p>
  ) : null;
}

/** One account: its label, state, usage, and what can be done with it. */
function AccountRow({
  account,
  ai,
  now,
  timezone,
  busy,
  onReplace,
}: {
  account: AiConnection;
  ai: Ai;
  now: number;
  timezone: string;
  busy: boolean;
  onReplace: (verb: string) => void;
}) {
  const state = accountState(account, ai, now, timezone);
  return (
    <li
      aria-label={account.label}
      data-connection={account.id}
      data-state={state.key}
      className="flex flex-col gap-2.5 px-3.5 py-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="min-w-0 font-mono text-meta break-all text-foreground">
          {account.label}
        </span>
        <StatusLabel tone={state.tone} className="text-2xs font-semibold tracking-wider uppercase">
          <span data-account-state>{state.text}</span>
        </StatusLabel>
      </div>
      <Show when={state.reason}>
        <p className="text-meta text-brick">{state.reason}</p>
      </Show>
      <ExpiryNote account={account} now={now} timezone={timezone} />
      {account.usage ? <UsageLines usage={account.usage} now={now} timezone={timezone} /> : null}
      <AccountActions account={account} ai={ai} state={state} busy={busy} onReplace={onReplace} />
    </li>
  );
}

function RoleChips({ card }: { card: ProviderCard }) {
  const chips = roleChips(card.roles);
  return (
    <Show when={chips.length > 0}>
      <div className="flex flex-wrap gap-1.5" data-chips>
        {chips.map((chip) => (
          <StatusBadge key={chip} tone={chip === "Default" ? "done" : "working"}>
            {chip}
          </StatusBadge>
        ))}
      </div>
    </Show>
  );
}

/** A closed row's usage: the fullest window's bar, the balance, or where the usage is. */
function RowUsageView({ usage }: { usage: RowUsage }) {
  if (usage.kind === "bar") {
    return (
      <div className="flex flex-col gap-1.5" data-row-usage="bar">
        <div className="flex justify-between font-mono text-2xs text-muted-foreground">
          <span>{usage.label}</span>
          <span>{usage.pct}%</span>
        </div>
        <Progress
          value={usage.pct}
          aria-label={`${usage.label}, ${usage.pct}% used`}
          className="[&_[data-slot=progress-indicator]]:bg-foreground"
        />
      </div>
    );
  }
  if (usage.kind === "text") {
    return (
      <p className="font-mono text-2xs text-muted-foreground" data-row-usage="text">
        {usage.text}
      </p>
    );
  }
  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-0.5 sm:flex-col sm:items-start"
      data-row-usage="link"
    >
      <Show when={usage.unshared}>
        <span className="font-mono text-2xs text-muted-foreground">Usage not shared</span>
      </Show>
      <a
        href={usage.href}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-meta font-medium whitespace-nowrap text-copper underline-offset-4 hover:underline"
      >
        See on {usage.site}
        <ArrowUpRight className="size-3" />
      </a>
    </div>
  );
}

type RowProps = {
  card: ProviderCard;
  ai: Ai;
  now: number;
  timezone: string;
  busy: boolean;
  anchor: boolean;
  open: boolean;
  slot: ReactNode;
  onToggle: () => void;
  onOpen: (open: Open) => void;
};

/** A closed row: the provider, its usage and its state; the chevron opens it. */
function RowHeader({
  card,
  ai,
  now,
  timezone,
  open,
  bodyId,
  onToggle,
}: Pick<RowProps, "card" | "ai" | "now" | "timezone" | "open" | "onToggle"> & { bodyId: string }) {
  const { provider } = card;
  const name = PROVIDERS[provider].name;
  const status = rowStatus(card, ai, now, timezone);
  return (
    // The chevron is the control; the rest of the row is a bigger target for a pointer.
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="grid cursor-pointer grid-cols-[auto_1fr_auto_auto] items-center gap-x-3 gap-y-2 py-3.5 sm:grid-cols-[auto_1fr_10.5rem_9rem_auto] sm:gap-x-3.5"
      onClick={onToggle}
    >
      <ProviderLogo provider={provider} className="size-8" />
      <div className="flex min-w-0 flex-col">
        <span className="text-[15px] leading-5 font-medium text-foreground">{name}</span>
        <span className="truncate text-meta text-muted-foreground">{rowSubtitle(card)}</span>
      </div>
      <div className="col-span-4 col-start-1 row-start-2 pl-11 sm:col-span-1 sm:col-start-3 sm:row-start-1 sm:pl-0">
        <RowUsageView usage={rowUsage(card, ai, now)} />
      </div>
      <div className="flex justify-end">
        <StatusBadge tone={status.tone} data-row-status className="h-6 rounded-full px-2.5">
          {status.text}
        </StatusBadge>
      </div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        aria-label={`${name} accounts`}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
      </button>
    </div>
  );
}

/** An open row: what it's used for, its accounts, Add another account, and its usage page. */
function RowBody({
  card,
  ai,
  now,
  timezone,
  busy,
  slot,
  bodyId,
  onOpen,
}: Omit<RowProps, "anchor" | "open" | "onToggle"> & { bodyId: string }) {
  const { provider, accounts } = card;
  const name = PROVIDERS[provider].name;
  const usage = usageLink(provider, accounts);
  const order = orderNote(card, ai);
  const unshared = rowUsage(card, ai, now);
  const add = `Add another ${name} account`;
  return (
    <div id={bodyId} className="flex flex-col gap-3 pb-4 sm:pl-11.5">
      <RoleChips card={card} />
      <Show when={order}>
        <p className="text-meta text-muted-foreground">{order}</p>
      </Show>
      {unshared.kind === "link" && unshared.unshared ? (
        <p className="text-meta text-muted-foreground" data-usage-unshared>
          {name} doesn't report usage to Crew.
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-border rounded-lg bg-muted/50 ring-1 ring-foreground/8">
        {accounts.map((account) => (
          <AccountRow
            key={account.id}
            account={account}
            ai={ai}
            now={now}
            timezone={timezone}
            busy={busy}
            onReplace={(verb) =>
              onOpen({ provider, account: account.id, title: `${verb} ${account.label}` })
            }
          />
        ))}
      </ul>
      {slot}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* One Claude subscription, or Workers AI account, at a time: a new credential is a Replace. */}
        {oneAccount(provider) ? (
          <span />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => onOpen({ provider, title: add })}
            aria-label={add}
            className="-ml-2 text-muted-foreground"
          >
            <Plus />
            {add}
          </Button>
        )}
        <a
          href={usage.href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 py-1 text-meta text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          {usage.text}
          <ExternalLink className="size-3" />
        </a>
      </div>
    </div>
  );
}

/** One connected provider (§6.1): a row that opens to its accounts. */
function ProviderRow(props: RowProps) {
  const { card, anchor, open } = props;
  const bodyId = useId();
  return (
    <section
      aria-label={PROVIDERS[card.provider].name}
      id={anchor ? "ai-default" : undefined}
      data-provider-card={card.provider}
      data-open={open}
      className="scroll-mt-6 border-t border-border"
    >
      <RowHeader {...props} bodyId={bodyId} />
      <Show when={open}>
        <RowBody {...props} bodyId={bodyId} />
      </Show>
    </section>
  );
}

/**
 * Which rows are open: what the owner chose, else whether the row opens by
 * itself: the only row, a problem or a connect, the connect panel's, or the
 * one a thread's Reconnect landed on (`#ai-default`). A row that opened by
 * itself stays open once that's over, so a connect's result shows where it
 * ran, until the owner closes it.
 */
function useOpenRows(auto: Partial<Record<AiProvider, boolean>>) {
  const [chosen, setChosen] = useState<Partial<Record<AiProvider, boolean>>>({});
  const opened = (Object.keys(auto) as AiProvider[]).filter(
    (p) => auto[p] && chosen[p] === undefined,
  );
  // Kept in render, as `/api/me` moves: each row once.
  if (opened.length > 0) {
    setChosen((c) => ({ ...c, ...Object.fromEntries(opened.map((p) => [p, true])) }));
  }
  const isOpen = (provider: AiProvider) => chosen[provider] ?? auto[provider] ?? false;
  const toggle = (provider: AiProvider) =>
    setChosen((c) => ({ ...c, [provider]: !isOpen(provider) }));
  return { isOpen, toggle };
}

/** The Providers card: Add a provider, then a row per connected provider. */
export function ProvidersCard({
  ai,
  cards,
  now,
  timezone,
  busy,
  anchor,
  slotFor,
  orphan,
  canAdd,
  onAdd,
  onOpen,
}: {
  ai: Ai;
  cards: ProviderCard[];
  now: number;
  timezone: string;
  busy: boolean;
  /** The default model's provider: `#ai-default`. */
  anchor: AiProvider | null;
  /** The connect panel, in the row it's for. */
  slotFor: (provider: AiProvider) => ReactNode;
  /** The connect panel when it's for no row yet (a sign-in from before a reload). */
  orphan: ReactNode;
  canAdd: boolean;
  onAdd: () => void;
  onOpen: (open: Open) => void;
}) {
  const hashed = window.location.hash === "#ai-default";
  const auto = Object.fromEntries(
    cards.map((card) => [
      card.provider,
      cards.length === 1 ||
        rowStatus(card, ai, now, timezone).open ||
        slotFor(card.provider) !== null ||
        (hashed && card.provider === anchor),
    ]),
  );
  const rows = useOpenRows(auto);
  return (
    <Card role="region" aria-label="Providers" data-providers>
      <CardHeader>
        <CardTitle>Providers</CardTitle>
        <CardDescription>The accounts your crew thinks with.</CardDescription>
        <Show when={canAdd}>
          <CardAction>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={onAdd}
              aria-label="Add a provider"
            >
              <Plus />
              <span className="hidden sm:inline">Add a provider</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </CardAction>
        </Show>
      </CardHeader>
      <CardContent className="flex flex-col">
        <Show when={cards.length === 0 && !orphan}>
          <p className="border-t border-border pt-4 text-meta text-muted-foreground">
            Nothing connected yet. Add a provider to give your crew a mind to think with.
          </p>
        </Show>
        {cards.map((card) => {
          const open = rows.isOpen(card.provider);
          return (
            <ProviderRow
              key={card.provider}
              card={card}
              ai={ai}
              now={now}
              timezone={timezone}
              busy={busy}
              anchor={card.provider === anchor}
              open={open}
              slot={slotFor(card.provider)}
              onToggle={() => rows.toggle(card.provider)}
              onOpen={onOpen}
            />
          );
        })}
        {orphan}
      </CardContent>
    </Card>
  );
}
